import { useEffect, useRef, useState } from 'react'
import { Markdown } from './Markdown'

// ── Release manifest types (mirror website/backend/releases/store.go) ──
type Asset = {
  platform: string
  // "download" (the human .dmg/.tar.gz button) or "update" (the self-updater's
  // signed .app archive). Absent means download (pre-0.3 single-asset manifests).
  // macOS ships both for one platform string, so the page must hide kind=update.
  kind?: string
  url: string
  sha256: string
  signature: string
  size_bytes: number
}

type Release = {
  version: string
  published_at: string
  channel: string
  changelog_md: string
  assets: Asset[]
}

const PLATFORM_LABEL: Record<string, string> = {
  'macos-arm64': 'macOS · Apple Silicon',
  'macos-x86_64': 'macOS · Intel',
  'linux-x86_64': 'Linux · x86_64',
  'windows-x86_64': 'Windows · x86_64',
}

function detectPlatform(): string {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('mac')) {
    return navigator.userAgent.includes('Intel') ? 'macos-x86_64' : 'macos-arm64'
  }
  if (ua.includes('win')) return 'windows-x86_64'
  if (ua.includes('linux')) return 'linux-x86_64'
  return 'macos-arm64'
}

function fmtSize(bytes: number): string {
  if (!bytes) return ''
  return `${(bytes / 1_000_000).toFixed(1)} MB`
}

// ── Instrument trace path: a decaying, phase-shifted sinusoid drawn across the
// hero "scope screen". Generated once, deterministic, so the SVG stroke-draw
// animation has a stable target. ──
function tracePath(w: number, h: number, samples = 240): string {
  const mid = h / 2
  let d = ''
  for (let i = 0; i <= samples; i++) {
    const t = i / samples
    const x = t * w
    // Two superposed modes with a slow envelope — reads as a real RF waveform.
    const env = Math.exp(-Math.pow((t - 0.5) * 2.4, 2)) * 0.62 + 0.38
    const y =
      mid -
      env *
        (h * 0.34) *
        (Math.sin(t * Math.PI * 7.5) * 0.7 + Math.sin(t * Math.PI * 18 + 0.6) * 0.3)
    d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `
  }
  return d.trim()
}

// Reveal-on-scroll: adds `is-in` once the node enters the viewport. Entrance
// motion itself is gated in CSS behind prefers-reduced-motion, so screenshots
// (which run with reduced motion) always see the final, settled state.
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            node.classList.add('is-in')
            io.unobserve(node)
          }
        })
      },
      { threshold: 0.18 },
    )
    io.observe(node)
    return () => io.disconnect()
  }, [])
  return ref
}

// ── Static content ──
const FEATURES: { tag: string; title: string; readout: string; body: string }[] = [
  {
    tag: 'SOLVER',
    title: 'Method of Moments',
    readout: 'thin-wire · reduced kernel',
    body: 'A full MoM engine solves the wire currents directly — not a lookup table, not a curve fit. Feed impedance, gain, and pattern fall out of the same matrix.',
  },
  {
    tag: 'VALIDATION',
    title: 'Cross-checked vs NEC-2',
    readout: 'Δ|Z| < 1% on pinned decks',
    body: 'Every antenna model carries a regression test pinning it against NEC-2 and the textbooks — Balanis, Kraus, the ARRL Antenna Handbook. Math correctness is the product.',
  },
  {
    tag: 'VIEWPORT',
    title: '3D geometry & pattern',
    readout: 'orbit · drag · pick',
    body: 'Build wire structures in a real 3D viewport. Orbit the geometry, drag handles to resize, then flip to the radiation pattern rendered from the solved currents.',
  },
  {
    tag: 'DIAGNOSTICS',
    title: 'Delta diagnostics',
    readout: 'rank Δ-gap culprits',
    body: 'Drop a measured Touchstone trace and hamtenna ranks which assumption — element length, wire radius, ground σ, height — closes the gap to your measurement.',
  },
  {
    tag: 'SWEEP',
    title: 'SWR & impedance',
    readout: 'R + jX vs frequency',
    body: 'Sweep the band and read resistance, reactance, and VSWR per frequency — the resonance and bandwidth picture, straight from closed-form impedance.',
  },
  {
    tag: 'GROUND',
    title: 'Real-ground models',
    readout: 'εr · σ · height AGL',
    body: 'Free space, perfect ground, or real soil with an RC-image model. Height above ground and conductivity shift the pattern and feed-Z the way they do on the air.',
  },
]

const DOCS: { n: string; title: string; body: string }[] = [
  {
    n: '01',
    title: 'Getting started',
    body: 'Download the build for your platform, launch it, and you land in the Studio with a default dipole array. Edit elements in the left column, read results on the right, orbit the geometry in the centre viewport. Projects stay on your machine — nothing is uploaded.',
  },
  {
    n: '02',
    title: 'Supported antenna models',
    body: 'Straight thin-wire elements and multi-element arrays (driven, reflector, director), elevated dipoles over real ground, and wire networks with junctions. Each element carries a real conductor radius feeding the impedance and SWR sweep.',
  },
  {
    n: '03',
    title: 'Accuracy & validation',
    body: 'The solver is pinned against the nec2c reference and textbook closed forms. Where the reduced-kernel MoM and induced-EMF differ (e.g. a centre-fed dipole reads ≈80 Ω solved vs 73 Ω induced-EMF), the discrepancy is documented and tested, not hidden. The cross-check decks ship with the source.',
  },
  {
    n: '04',
    title: 'Self-update & trust',
    body: 'Releases are built on a workstation — never CI — and signed with an ed25519 key whose public half is embedded in the app. The updater verifies every artifact’s signature against that trust anchor before installing, so a compromised build server cannot ship you a malicious update.',
  },
]

const REFERENCES = ['Balanis · Antenna Theory', 'Kraus · Antennas', 'ARRL Antenna Handbook', 'NEC-2 / nec2c']

// ── Mobile beta channels. The same Studio ships on phone + tablet; both builds
// are in store-run testing programs, so the join path is a tester opt-in link,
// not a download. iOS rides the phosphor-trace channel, Android the teal one —
// the two signature instrument colours, one per platform. ──
const BETA: {
  glyph: 'apple' | 'android'
  tag: string
  title: string
  readout: string
  body: string
  cta: string
  href: string
}[] = [
  {
    glyph: 'apple',
    tag: 'iOS · TESTFLIGHT',
    title: 'iPhone & iPad',
    readout: 'public link · auto-updates',
    body: 'Install through Apple TestFlight — get the TestFlight app first, then open the link to add hamtenna. New builds arrive on their own.',
    cta: 'Join on TestFlight',
    href: 'https://testflight.apple.com/join/WA6De8s1',
  },
  {
    glyph: 'android',
    tag: 'ANDROID · GOOGLE PLAY',
    title: 'Android',
    readout: 'closed testing · play store',
    body: 'Opt in to closed testing on Google Play, then install hamtenna like any other app and update through the Play Store.',
    cta: 'Join on Google Play',
    href: 'https://play.google.com/apps/testing/blue.mikael.hamtenna',
  },
]

// ── Smith-chart-flavoured decorative SVG for the accuracy section ──
function SmithChart() {
  // Resistance circles (centres on the real axis) + reactance arcs, unit circle.
  const R = 120
  const cx = 130
  const cy = 130
  const rCircles = [0.0, 0.5, 1.0, 2.0, 5.0]
  const xArcs = [0.5, 1.0, 2.0, 5.0]
  return (
    <svg className="smith" viewBox="0 0 260 260" aria-hidden="true">
      <circle cx={cx} cy={cy} r={R} className="smith-unit" />
      <line x1={cx - R} y1={cy} x2={cx + R} y2={cy} className="smith-axis" />
      {rCircles.map((r) => {
        const rad = R / (1 + r)
        const ccx = cx + R - rad
        return <circle key={`r${r}`} cx={ccx} cy={cy} r={rad} className="smith-r" />
      })}
      {xArcs.flatMap((x) => {
        const rad = R / x
        return [1, -1].map((s) => {
          const ccx = cx + R
          const ccy = cy - s * rad
          return (
            <circle
              key={`x${x}-${s}`}
              cx={ccx}
              cy={ccy}
              r={rad}
              className="smith-x"
            />
          )
        })
      })}
      <circle cx={cx} cy={cy} r="3" className="smith-dot" />
    </svg>
  )
}

// Platform glyphs — drawn in currentColor so the card's channel colour drives
// them (iOS phosphor-green, Android teal). Decorative; the tag carries the name.
function AppleGlyph() {
  return (
    <svg className="beta-glyph" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.05 12.04c-.03-2.43 1.99-3.6 2.08-3.66-1.13-1.66-2.89-1.89-3.52-1.92-1.5-.15-2.93.88-3.69.88-.76 0-1.93-.86-3.18-.84-1.64.02-3.15.95-3.99 2.42-1.7 2.95-.43 7.32 1.22 9.72.81 1.17 1.78 2.49 3.04 2.44 1.22-.05 1.68-.79 3.16-.79 1.47 0 1.89.79 3.18.76 1.31-.02 2.14-1.19 2.94-2.37.93-1.36 1.31-2.68 1.33-2.75-.03-.01-2.55-.98-2.58-3.89z" />
      <path d="M14.69 4.81c.67-.82 1.13-1.95 1-3.06-.99.04-2.19.66-2.89 1.47-.62.72-1.17 1.87-1.02 2.97 1.11.09 2.24-.56 2.91-1.38z" />
    </svg>
  )
}

function AndroidGlyph() {
  return (
    <svg className="beta-glyph beta-glyph--teal" viewBox="0 0 24 24" aria-hidden="true">
      <line x1="7" y1="3.6" x2="8.7" y2="6.4" />
      <line x1="17" y1="3.6" x2="15.3" y2="6.4" />
      <path d="M5 12.5a7 7 0 0 1 14 0z" />
      <circle cx="9.2" cy="9.6" r="0.95" />
      <circle cx="14.8" cy="9.6" r="0.95" />
    </svg>
  )
}

function HeroScope() {
  const w = 760
  const h = 280
  const d = tracePath(w, h)
  return (
    <div className="scope" aria-hidden="true">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="scope-svg">
        <defs>
          <pattern id="grat" width="38" height="38" patternUnits="userSpaceOnUse">
            <path d="M38 0H0V38" fill="none" className="grat-line" />
          </pattern>
        </defs>
        <rect width={w} height={h} fill="url(#grat)" />
        <line x1={w / 2} y1="0" x2={w / 2} y2={h} className="grat-axis" />
        <line x1="0" y1={h / 2} x2={w} y2={h / 2} className="grat-axis" />
        <path d={d} className="scope-trace" />
      </svg>
      <div className="scope-readout">
        <span>CH1</span>
        <span>14.200 MHz</span>
        <span>50 Ω</span>
        <span className="live">● LIVE</span>
      </div>
    </div>
  )
}

// ── Privacy policy: served at /privacy via the backend's SPA fallback. The
// store listings (Play / App Store) point here, so the URL must stay stable. ──
const PRIVACY_SECTIONS: { n: string; title: string; body: string[] }[] = [
  {
    n: '01',
    title: 'The desktop app',
    body: [
      'HamTenna runs entirely on your machine. Antenna designs, solver results, and project files are stored locally and never uploaded. There are no accounts, no analytics, and no telemetry.',
      'The only network call the desktop app makes is the self-update check: it fetches the public release manifest from this website over HTTPS. That request carries no identifier beyond what any HTTPS request carries (your IP address, which lands in standard, short-lived server access logs). Nothing about your projects or usage is transmitted.',
    ],
  },
  {
    n: '02',
    title: 'The mobile apps',
    body: [
      'The iOS and Android apps are fully offline. They make no network calls at all — no update check (the App Store and Google Play own update delivery), no analytics, no crash reporting, no third-party SDKs, no ads.',
      'Your antenna designs stay on your device. We could not read them if we wanted to.',
    ],
  },
  {
    n: '03',
    title: 'This website',
    body: [
      'The website serves downloads and the release changelog. Like every web server, it keeps standard access logs (IP address, user agent, requested path) for operational purposes; they are not used for tracking and are not shared with anyone.',
      'There are no cookies, no analytics scripts, and no third-party embeds.',
    ],
  },
  {
    n: '04',
    title: 'Contact & changes',
    body: [
      'HamTenna is built by Grön IT AB, Sweden. Questions about this policy: gronitab@gmail.com.',
      'If this policy ever changes, the change will be visible at this URL and noted in the release changelog. Effective date: 2026-06-11.',
    ],
  },
]

function Privacy() {
  return (
    <div className="page">
      <div className="scanlines" aria-hidden="true" />

      <nav className="topbar">
        <a className="brand" href="/">
          <span className="brand-mark" aria-hidden="true" />
          hamtenna
        </a>
        <div className="navlinks">
          <a href="/">← Back to downloads</a>
        </div>
        <span className="status-chip">PRIVACY</span>
      </nav>

      <main className="privacy">
        <header className="privacy-head">
          <span className="eyebrow">EMISSIONS REPORT · DATA COLLECTION</span>
          <h1>Privacy policy</h1>
          <p className="tagline">HamTenna collects no personal data.</p>
        </header>

        <div className="tx-meter" role="img" aria-label="Telemetry transmitted: zero bytes">
          <div className="tx-row">
            <span className="tx-label">TELEMETRY TX</span>
            <span className="tx-bar" aria-hidden="true" />
            <span className="tx-value">0 bytes</span>
          </div>
          <div className="tx-row">
            <span className="tx-label">ANALYTICS</span>
            <span className="tx-bar" aria-hidden="true" />
            <span className="tx-value">none</span>
          </div>
          <div className="tx-row">
            <span className="tx-label">ACCOUNTS</span>
            <span className="tx-bar" aria-hidden="true" />
            <span className="tx-value">none</span>
          </div>
          <div className="tx-row tx-row--live">
            <span className="tx-label">YOUR DESIGNS</span>
            <span className="tx-bar" aria-hidden="true" />
            <span className="tx-value">● LOCAL ONLY</span>
          </div>
        </div>

        <div className="privacy-sections">
          {PRIVACY_SECTIONS.map((s) => (
            <article key={s.n} className="privacy-section">
              <span className="doc-n">{s.n}</span>
              <h2>{s.title}</h2>
              {s.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </article>
          ))}
        </div>
      </main>

      <footer>
        <span className="foot-mark" aria-hidden="true" />
        <p>hamtenna · free to download · not open source</p>
        <p className="foot-credit">
          <a href="https://xn--grn-tna.it/" rel="author">Grön IT AB</a>
        </p>
      </footer>
    </div>
  )
}

export function App() {
  if (window.location.pathname.replace(/\/+$/, '') === '/privacy') {
    return <Privacy />
  }
  return <Landing />
}

function Landing() {
  const [latest, setLatest] = useState<Release | null>(null)
  const [history, setHistory] = useState<Release[]>([])
  const [error, setError] = useState<string | null>(null)
  const myPlatform = detectPlatform()

  useEffect(() => {
    // fetch does not reject on HTTP error statuses, so guard on r.ok: a 404
    // (no releases yet) or a 5xx error envelope must both fall to the empty
    // state, never get parsed as a release ('BUILD v undefined' + Invalid Date).
    fetch('/api/releases/latest')
      .then((r) => (r.ok ? r.json() : null))
      .then(setLatest)
      .catch((e) => setError(String(e)))
    fetch('/api/releases')
      .then((r) => (r.ok ? r.json() : { releases: [] }))
      .then((j) => setHistory(j.releases ?? []))
      .catch(() => undefined)
  }, [])

  // The landing page only shows human download assets; kind=update is the
  // self-updater's channel (the .app archive) and must never appear as a button.
  const downloads = (latest?.assets ?? []).filter((a) => a.kind !== 'update')
  const primary = downloads.find((a) => a.platform === myPlatform) ?? downloads[0]

  const featuresRef = useReveal<HTMLElement>()
  const accuracyRef = useReveal<HTMLElement>()
  const docsRef = useReveal<HTMLElement>()
  const betaRef = useReveal<HTMLElement>()

  return (
    <div className="page">
      <div className="scanlines" aria-hidden="true" />

      <nav className="topbar">
        <a className="brand" href="#top">
          <span className="brand-mark" aria-hidden="true" />
          hamtenna
        </a>
        <div className="navlinks">
          <a href="#features">Features</a>
          <a href="#accuracy">Accuracy</a>
          <a href="#docs">Docs</a>
          <a href="#beta">Beta</a>
          <a href="#changelog">Changelog</a>
        </div>
        <span className="status-chip">
          {latest ? `BUILD v${latest.version}` : 'BUILD —'}
        </span>
      </nav>

      <header className="hero" id="top">
        <div className="hero-inner">
          <div className="hero-copy">
            <span className="eyebrow">RF ANTENNA WORKBENCH · DESKTOP</span>
            <h1>hamtenna</h1>
            <p className="tagline">
              Design and simulate antennas in 3D — with math you can verify.
            </p>
            <p className="subtagline">
              A Method-of-Moments solver for ham radio, cross-checked against NEC-2 and the
              textbooks. Every model ships pinned to a reference. The math is the product.
            </p>

            {latest ? (
              <div className="cta">
                {primary ? (
                  <a className="download" href={primary.url}>
                    <span className="dl-key">↧</span>
                    <span className="dl-text">
                      Download for {PLATFORM_LABEL[primary.platform] ?? primary.platform}
                    </span>
                    <span className="dl-meta">{fmtSize(primary.size_bytes)}</span>
                  </a>
                ) : (
                  <p className="version">No build available for your platform yet.</p>
                )}
                <p className="version">
                  v{latest.version} · {new Date(latest.published_at).toLocaleDateString()} ·{' '}
                  {latest.channel}
                </p>
                <details className="other-platforms">
                  <summary>Other platforms</summary>
                  <ul>
                    {downloads
                      .filter((a) => a.platform !== primary?.platform)
                      .map((a) => (
                        <li key={a.platform}>
                          <a href={a.url}>{PLATFORM_LABEL[a.platform] ?? a.platform}</a>
                          <span className="size">{fmtSize(a.size_bytes)}</span>
                        </li>
                      ))}
                  </ul>
                </details>
              </div>
            ) : (
              <div className="cta">
                <p className="version">No releases published yet. Check back soon.</p>
                <a className="download download--ghost" href="#features">
                  <span className="dl-text">See what it does</span>
                </a>
              </div>
            )}
            {error && <p className="error">{error}</p>}
            <p className="freebadge">Free to download · not open source · your designs stay local</p>
          </div>

          <HeroScope />
        </div>
      </header>

      <section className="features" id="features" ref={featuresRef}>
        <div className="section-head">
          <span className="section-idx">[ 01 ]</span>
          <h2>What it does</h2>
          <span className="section-rule" aria-hidden="true" />
        </div>
        <ul className="feature-grid">
          {FEATURES.map((f) => (
            <li key={f.title} className="panel">
              <span className="panel-corner panel-corner--tl" aria-hidden="true" />
              <span className="panel-corner panel-corner--br" aria-hidden="true" />
              <div className="panel-tag">{f.tag}</div>
              <h3>{f.title}</h3>
              <div className="panel-readout">{f.readout}</div>
              <p>{f.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="accuracy" id="accuracy" ref={accuracyRef}>
        <div className="section-head">
          <span className="section-idx">[ 02 ]</span>
          <h2>Accuracy is the feature</h2>
          <span className="section-rule" aria-hidden="true" />
        </div>
        <div className="accuracy-body">
          <div className="accuracy-copy">
            <p>
              Most antenna apps give you a pretty pattern and ask you to trust it. hamtenna gives
              you a number and shows its work. The solver is pinned, in source, against an
              independent NEC-2 build and against closed-form textbook results — and where two
              correct methods legitimately disagree, that delta is documented and tested rather
              than papered over.
            </p>
            <div className="refchips">
              {REFERENCES.map((r) => (
                <span key={r} className="refchip">
                  {r}
                </span>
              ))}
            </div>
            <dl className="specs">
              <div>
                <dt>Driven dipole Z_in</dt>
                <dd>≈ 73 Ω (induced-EMF) · ≈ 80 Ω (MoM, pinned to nec2c)</dd>
              </div>
              <div>
                <dt>Ground model</dt>
                <dd>Free · Perfect · Real (RC image, εr + σ)</dd>
              </div>
              <div>
                <dt>Cross-check decks</dt>
                <dd>15 NEC-2 reference cases ship with the source</dd>
              </div>
            </dl>
          </div>
          <SmithChart />
        </div>
      </section>

      <section className="docs" id="docs" ref={docsRef}>
        <div className="section-head">
          <span className="section-idx">[ 03 ]</span>
          <h2>Documentation</h2>
          <span className="section-rule" aria-hidden="true" />
        </div>
        <div className="docs-grid">
          {DOCS.map((d) => (
            <article key={d.n} className="doc">
              <span className="doc-n">{d.n}</span>
              <h3>{d.title}</h3>
              <p>{d.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="beta" id="beta" ref={betaRef}>
        <div className="section-head">
          <span className="section-idx">[ 04 ]</span>
          <h2>Join the beta</h2>
          <span className="section-rule" aria-hidden="true" />
        </div>
        <p className="beta-lead">
          hamtenna runs on iPhone, iPad, and Android too — the full Studio, offline,
          nothing uploaded. The mobile builds are in store testing while we put them
          through their paces. Pick your platform to get on the list.
        </p>
        <ul className="beta-grid">
          {BETA.map((b) => (
            <li key={b.title} className="panel beta-card">
              <span className="panel-corner panel-corner--tl" aria-hidden="true" />
              <span className="panel-corner panel-corner--br" aria-hidden="true" />
              {b.glyph === 'apple' ? <AppleGlyph /> : <AndroidGlyph />}
              <div className="panel-tag">{b.tag}</div>
              <h3>{b.title}</h3>
              <div className="panel-readout">{b.readout}</div>
              <p>{b.body}</p>
              <a
                className="download download--ghost beta-cta"
                href={b.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="dl-text">{b.cta}</span>
                <span className="dl-key">↗</span>
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="changelog" id="changelog">
        <div className="section-head">
          <span className="section-idx">[ 05 ]</span>
          <h2>Changelog</h2>
          <span className="section-rule" aria-hidden="true" />
        </div>
        {history.length === 0 ? (
          <p className="empty-log">No releases yet.</p>
        ) : (
          <div className="log">
            {history.map((rel) => (
              <article key={rel.version} className="release">
                <div className="release-head">
                  <h3>
                    v{rel.version} <span className="channel">{rel.channel}</span>
                  </h3>
                  <p className="published">{new Date(rel.published_at).toLocaleString()}</p>
                </div>
                <Markdown className="md" source={rel.changelog_md} />
              </article>
            ))}
          </div>
        )}
      </section>

      <footer>
        <span className="foot-mark" aria-hidden="true" />
        <p>hamtenna · free to download · not open source</p>
        <p className="foot-credit">
          <a href="https://xn--grn-tna.it/" rel="author">Grön IT AB</a>
        </p>
      </footer>
    </div>
  )
}
